import { type ApiClient } from '@core/api/client'
import { useCallback } from 'react'

export enum MothersDayUserActions {
  DID_VIEW_MOTHERS_DAY_LP = 'DidViewMothersDayLp',
  DID_TAP_MOTHERS_DAY_CTA = 'DidTapMothersDayCta',
  DID_TAP_MOTHERS_DAY_CROSS_SELL_TILE = 'DidTapMothersDayCrossSellTile',
  DID_START_MOTHERS_DAY_PRODUCT_FLOW = 'DidStartMothersDayProductFlow',
}

enum UserActions {
  DID_VIEW_POD_LP = 'DidViewPodLp',
  DID_VIEW_DPF_LP = 'DidViewDpfLp',
  DID_TAP_POD_C2A = 'DidTapPodC2A',
  DID_VIEW_POD_STICKERS_LP = 'DidViewPodStickersLp',
  DID_TAP_POD_STICKERS_C2A = 'DidTapPodStickersC2A',
  DID_VIEW_POD_STICKERS_SHEET_LP = 'DidViewPodStickersSheetLp',
  DID_TAP_POD_STICKERS_SHEET_C2A = 'DidTapPodStickersSheetC2A',
  DID_VIEW_VALENTINES_DAY_LP = 'DidViewValentinesDayLp',
  DID_SELECT_POD_CATEGORY = 'DidSelectPodCategory',
  DID_SELECT_POD_PRODUCT = 'DidSelectPodProduct',
  DID_SELECT_POD_TEMPLATE = 'DidSelectPodTemplate',
  DID_TAP_PRODUCT_PAGE_C2A = 'DidTapProductPageC2A',
  DID_SKIP_PREVIEW = 'DidSkipPodPreview',
  DID_SELECT_PHOTOS = 'DidSelectPhotos',
  DID_VIEW_SELECT_TEMPLATE_PAGE = 'DidViewSelectTemplatePage',
  DID_TAP_CART_KEEP_SHOPPING = 'DidTapCartKeepShopping',
  DID_CLICK_SMOKESCREEN_C2A = 'DidClickSmokescreenC2A',
  DID_VIEW_SMOKESCREEN_LP = 'DidViewSmokescreenLp',
  DID_TAP_SOCKS_C2A = 'DidTapSocksC2A',
  DID_COPY_SOCKS_PROMO_CODE = 'DidCopySocksPromoCode',
  DID_TAP_EGIFTS_C2A = 'DidTapEGiftsC2A',
  DID_TAP_C2A = 'DidTapC2A',
  DID_SELECT_PHOTO_PRODUCT = 'DidSelectPhotoProduct', // legacy, for old divvup/pod combined page
  DID_TAP_PREVIEW = 'DidTapPreviewPhotoProduct',
  DID_TAP_SHOPPING_CART = 'DidTapShoppingCart', // should be tapping the icon, add the number of items in the cart
  DID_ADD_PRODUCT_TO_CART = 'DidAddProductToCart', // When the user adds a product to the cart, add numOfProducts in cart for the toal after adding
  DID_TAP_TO_GET_TO_CHECKOUT = 'DidTapToGetToCheckout', // Add the number of items in cart
  DID_COMPLETE_CHECKOUT = 'DidCompleteCheckout',
  POD_PREVIEW_SUCCESS = 'PodPreviewSuccess',
  POD_PREVIEW_FAILURE = 'PodPreviewFailure',
  DID_RESUME_CART = 'DidResumeCart',
  DID_TAP_DPF_C2A = 'DidTapDpfC2A',
  // E-Gifts
  SELECTED_GIFT_CARD = 'SelectedGiftCard',
  SEARCHED_GIFT_CARD = 'SearchedGiftCard',
  DID_TAP_VIDEO = 'DidTapVideo',
  DID_CLOSE_VIDEO = 'DidCloseVideo',
  DID_SELECT_PREWRITTEN_MESSAGE = 'DidSelectPrewrittenMessage',
  VIEWED_DGC_DEMO = 'ViewedDgcDemo',
  SUBMITTED_SELF_BIRTHDAY = 'SubmittedSelfBirthday',
  CLICKED_CELEBRATION_DUPLICATED = 'ClickedCelebrationDuplicated',
  MUTE_SELF_BIRTHDAY_COLLECTION = 'MuteSelfBirthdayCollection',
  // Storybook
  DID_VIEW_STORYBOOK_LP = 'DidViewStorybookLp',
  DID_TAP_STORYBOOK_CTA = 'DidTapStorybookCta',
  DID_TAP_STORYBOOK_MILESTONE_CTA = 'DidTapStorybookMilestoneCta',
  DID_SELECT_STORYBOOK_DESIGN = 'DidSelectStorybookDesign',
  DID_START_STORYBOOK_CREATION = 'DidStartStorybookCreation',
  DID_COMPLETE_STORYBOOK_PAGE = 'DidCompleteStorybookPage',
  DID_SELECT_PHOTO_FOR_STORYBOOK = 'DidSelectPhotoForStorybook',
  DID_SELECT_BULK_PHOTOS_FOR_STORYBOOK = 'DidSelectBulkPhotosForStorybook',
  DID_TAP_EDIT_SPREAD_FROM_OVERVIEW = 'DidTapEditSpreadFromOverview',
  DID_ADD_STORYBOOK_TO_CART = 'DidAddStorybookToCart',
  DID_COMPLETE_STORYBOOK_CHECKOUT = 'DidCompleteStorybookCheckout',
  DID_RESUME_STORYBOOK_DRAFT = 'DidResumeStorybookDraft',
  DID_RESTART_STORYBOOK = 'DidRestartStorybook',
}

export type PhotoSource = 'family-album' | 'device'

type TrackingParams = {
  clickedElement?: string
  providerProductId?: number
  productName?: string
  productPrintProvider?: string
  slot?: string
  numberOfPhotos?: number
  numberOfPhotosReady?: number
  numberOfProductsInCart?: number
  cartId?: number
  skippedPodSubCategory?: boolean
  pageVariant?: string
  isShowingUserPhotos?: boolean
  kvType?: 'user_photo' | 'fallback_image'
  podCategory?: string
  productVariantId?: number
  podTemplateId?: number | string
  podTemplatePosition?: number
  photoSource?: PhotoSource
  photosSelected?: number
  photosRequired?: number
  giftCardCode?: string
  dgcName?: string
}

export type C2AMetadata = TrackingParams

const useRecordUserAction = (api: ApiClient, session: object) => {
  const recordUserAction = useCallback(
    (action: string, additionalMetadata: object = {}) => {
      const metadata = Object.assign(
        {
          pathname: window.location.pathname,
          url: window.location.href,
        },
        additionalMetadata,
        session
      )
      return api.logUserAction(action, metadata)
    },
    [api, session]
  )

  return {
    recordSmokeScreenC2ATap: (smokescreen: string) =>
      recordUserAction(UserActions.DID_CLICK_SMOKESCREEN_C2A, {
        smokescreen,
      }),
    recordDidViewSmokescreenLp: (smokescreen: string) =>
      recordUserAction(UserActions.DID_VIEW_SMOKESCREEN_LP, {
        smokescreen,
      }),
    recordDidTapSocksC2A: (metadata: TrackingParams) =>
      recordUserAction(UserActions.DID_TAP_SOCKS_C2A, metadata),
    recordDidCopySocksPromoCodeTap: () => recordUserAction(UserActions.DID_COPY_SOCKS_PROMO_CODE),
    recordDidTapEGiftsC2A: (metadata: TrackingParams) =>
      recordUserAction(UserActions.DID_TAP_EGIFTS_C2A, metadata),
    recordDidTapC2A: (metadata: TrackingParams) =>
      recordUserAction(UserActions.DID_TAP_C2A, metadata),

    photoProducts: {
      recordDidSkipPreview: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_SKIP_PREVIEW, metadata),
      recordDidViewPodLp: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_VIEW_POD_LP, metadata),
      recordDidViewDpfLp: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_VIEW_DPF_LP, metadata),
      recordDidViewPodStickersLp: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_VIEW_POD_STICKERS_LP, metadata),
      recordDidViewPodStickersSheetLp: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_VIEW_POD_STICKERS_SHEET_LP, metadata),
      recordDidViewValentinesDayLp: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_VIEW_VALENTINES_DAY_LP, metadata),
      recordDidTapPodC2A: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_TAP_POD_C2A, metadata),
      recordDidTapPodStickersC2A: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_TAP_POD_STICKERS_C2A, metadata),
      recordDidTapPodStickersSheetC2A: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_TAP_POD_STICKERS_SHEET_C2A, metadata),
      recordDidSelectPodCategory: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_SELECT_POD_CATEGORY, metadata),
      recordDidResumeCart: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_RESUME_CART, metadata),
      recordDidTapShoppingCart: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_TAP_SHOPPING_CART, metadata),
      recordDidAddProductToCart: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_ADD_PRODUCT_TO_CART, metadata),
      recordDidTapToGetToCheckout: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_TAP_TO_GET_TO_CHECKOUT, metadata),
      recordDidCompleteCheckout: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_COMPLETE_CHECKOUT, {
          ...metadata,
        }),
      recordDidSelectPhotoProduct: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_SELECT_PHOTO_PRODUCT, {
          ...metadata,
        }),

      recordDidTapProductPageC2A: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_TAP_PRODUCT_PAGE_C2A, {
          ...metadata,
        }),
      recordDidTapPreview: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_TAP_PREVIEW, {
          ...metadata,
        }),
      recordDidSelectPodProduct: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_SELECT_POD_PRODUCT, metadata),
      recordDidSelectPodTemplate: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_SELECT_POD_TEMPLATE, metadata),
      recordDidViewSelectTemplatePage: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_VIEW_SELECT_TEMPLATE_PAGE, metadata),
      recordDidSelectPhotos: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_SELECT_PHOTOS, metadata),
      recordPreviewSuccess: (metadata: TrackingParams) =>
        recordUserAction(UserActions.POD_PREVIEW_SUCCESS, metadata),
      recordPreviewFailure: (metadata: TrackingParams) =>
        recordUserAction(UserActions.POD_PREVIEW_FAILURE, metadata),
      recordDidTapCartKeepShopping: () => recordUserAction(UserActions.DID_TAP_CART_KEEP_SHOPPING),
      recordDidTapDpfC2A: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_TAP_DPF_C2A, metadata),
    },

    storybook: {
      recordDidViewStorybookLp: (metadata: TrackingParams) =>
        recordUserAction(UserActions.DID_VIEW_STORYBOOK_LP, metadata),
      recordDidTapStorybookCta: (metadata: { clickedElement: string }) =>
        recordUserAction(UserActions.DID_TAP_STORYBOOK_CTA, metadata),
      recordDidTapStorybookMilestoneCta: (metadata: { clickedElement: string }) =>
        recordUserAction(UserActions.DID_TAP_STORYBOOK_MILESTONE_CTA, metadata),
      recordDidSelectStorybookDesign: (metadata: { designId: number }) =>
        recordUserAction(UserActions.DID_SELECT_STORYBOOK_DESIGN, metadata),
      recordDidStartStorybookCreation: (metadata: { bookId: number }) =>
        recordUserAction(UserActions.DID_START_STORYBOOK_CREATION, metadata),
      recordDidCompleteStorybookPage: (metadata: { pageNumber: number }) =>
        recordUserAction(UserActions.DID_COMPLETE_STORYBOOK_PAGE, metadata),
      recordDidSelectPhotoForStorybook: (metadata: { source: 'FA' | 'DEVICE' }) =>
        recordUserAction(UserActions.DID_SELECT_PHOTO_FOR_STORYBOOK, metadata),
      recordDidSelectBulkPhotosForStorybook: (metadata: {
        bookId: number
        photoCount: number
        source: 'FA' | 'DEVICE'
      }) => recordUserAction(UserActions.DID_SELECT_BULK_PHOTOS_FOR_STORYBOOK, metadata),
      recordDidTapEditSpreadFromOverview: (metadata: { bookId: number; spreadIndex: number }) =>
        recordUserAction(UserActions.DID_TAP_EDIT_SPREAD_FROM_OVERVIEW, metadata),
      recordDidAddStorybookToCart: (metadata: { bookId: number; designId: number }) =>
        recordUserAction(UserActions.DID_ADD_STORYBOOK_TO_CART, metadata),
      recordDidCompleteStorybookCheckout: (metadata: {
        designId?: number
        quantity: number
        totalAmount?: number
      }) => recordUserAction(UserActions.DID_COMPLETE_STORYBOOK_CHECKOUT, metadata),
      recordDidResumeStorybookDraft: () => recordUserAction(UserActions.DID_RESUME_STORYBOOK_DRAFT),
      recordDidRestartStorybook: () => recordUserAction(UserActions.DID_RESTART_STORYBOOK),
    },

    egifts: {
      recordSelfBirthdaySubmitted: () => recordUserAction(UserActions.SUBMITTED_SELF_BIRTHDAY),
      recordSearchedGiftCard: (metadata: {
        q: string
        celebrationId: string
        numOfResults: number
      }) => recordUserAction(UserActions.SEARCHED_GIFT_CARD, metadata),
      recordSelectedGiftCard: (metadata: {
        q: string
        celebrationId: string
        giftCardCode: string
      }) => recordUserAction(UserActions.SELECTED_GIFT_CARD, metadata),
      recordViewedDgcDemo: () => recordUserAction(UserActions.VIEWED_DGC_DEMO),
      recordDidTapVideo: () => recordUserAction(UserActions.DID_TAP_VIDEO),
      recordDidCloseVideo: () => recordUserAction(UserActions.DID_CLOSE_VIDEO),
      recordC2ATap: (metadata?: C2AMetadata) =>
        recordUserAction(UserActions.DID_TAP_C2A, metadata || {}),
      recordPrewrittenMessageSelected: (metadata: object) =>
        recordUserAction(UserActions.DID_SELECT_PREWRITTEN_MESSAGE, metadata),
      recordClickedCelebrationDuplicate: (metadata: object) =>
        recordUserAction(UserActions.CLICKED_CELEBRATION_DUPLICATED, metadata),
      recordMuteSelfBirthdayCollection: () =>
        recordUserAction(UserActions.MUTE_SELF_BIRTHDAY_COLLECTION),
    },

    mothersDayLp: {
      recordDidViewMothersDayLp: (metadata: { variant: 'a' | 'b' | 'c' | 'd' | 'e' | 'f' }) =>
        recordUserAction(MothersDayUserActions.DID_VIEW_MOTHERS_DAY_LP, metadata),
      recordDidTapMothersDayCta: (metadata: {
        source: 'customize_now' | 'buy_now' | 'banner'
        variant: 'a' | 'b' | 'c' | 'd' | 'e' | 'f'
      }) => recordUserAction(MothersDayUserActions.DID_TAP_MOTHERS_DAY_CTA, metadata),
      recordDidTapMothersDayCrossSellTile: (metadata: {
        destinationCategory: string
        variant: 'a' | 'b' | 'c' | 'd' | 'e' | 'f'
      }) => recordUserAction(MothersDayUserActions.DID_TAP_MOTHERS_DAY_CROSS_SELL_TILE, metadata),
      recordDidStartMothersDayProductFlow: (metadata: {
        podCategory: string
        variant: 'a' | 'b' | 'c' | 'd' | 'e' | 'f'
      }) => recordUserAction(MothersDayUserActions.DID_START_MOTHERS_DAY_PRODUCT_FLOW, metadata),
    },
  }
}

export default useRecordUserAction
